/* ============================================================
   NTB Reporter

   Capture a barrier where it happens, lodge it later.

   Deliberate honesty: this prepares a submission, it does not file one.
   There is no backend, and an operator who believes a report was filed
   when it was not is worse off than one who knows they still have to
   file it. Every screen says so.

   The second job is quieter and possibly more valuable: an operator's
   own barrier log adds up to their private NTB tax — hours and dollars
   lost at specific points — which is exactly the evidence that makes an
   association or a corridor authority act.
   ============================================================ */

import {
  NTB_CATEGORIES,
  BARRIER_TYPES,
  LOCATIONS,
  AGENCIES,
  barrierType,
  formatSubmission,
  summarise
} from '../../data/barriers.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { money, number, shortDate, parseNumber } from '../../shared/format.js';
import { idb, newId } from '../../shared/storage.js';
import { saveRecord } from '../../shared/records.js';
import { toast } from '../../shared/toast.js';
import { PrintButton } from '../../components/PrintButton.js';

export function NtbPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>NTB Reporter</h1>
      <p>
        Record a non-tariff barrier where it happens, then lodge it at tradebarriers.africa
        when you have signal. Your log also totals what these barriers are costing you.
      </p>
    </header>

    <div class="notice notice-warning" style="margin-bottom: var(--ca-space-5);">
      <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
      <div style="font-size: var(--ca-text-xs);">
        <strong style="font-weight: var(--ca-weight-medium);">This prepares a report; it does not file one.</strong>
        Kanda has no server, so nothing can be transmitted on your behalf. The tool formats
        your report for the AfCFTA mechanism at
        <a href="https://tradebarriers.africa" target="_blank" rel="noopener noreferrer"
           style="color: var(--brand-accent-text);">tradebarriers.africa</a>
        — you copy it there. Under the EAC Elimination of Non-Tariff Barriers Act 2017,
        reported barriers carry defined resolution timelines. Unreported ones carry none.
      </div>
    </div>

    <div id="ntb-summary"></div>

    <details class="card" id="ntb-form-wrap" style="margin-bottom: var(--ca-space-5);">
      <summary style="cursor: pointer; font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">
        Record a barrier
      </summary>
      <div style="margin-top: var(--ca-space-4);">
        <label class="field">
          <span class="field-label">What happened</span>
          <select id="ntb-type" class="input-field">
            ${BARRIER_TYPES.map(
              (t) => html`<option value="${t.id}">${t.label}</option>`
            )}
          </select>
          <span class="field-hint" id="ntb-cat-hint"></span>
        </label>

        <div class="grid-2">
          <label class="field">
            <span class="field-label">Where</span>
            <select id="ntb-location" class="input-field">
              ${LOCATIONS.map((l) => html`<option value="${l}">${l}</option>`)}
            </select>
          </label>
          <label class="field">
            <span class="field-label">When</span>
            <input id="ntb-date" class="input-field" type="date" />
          </label>
        </div>

        <div class="grid-2">
          <label class="field">
            <span class="field-label">Hours lost</span>
            <input id="ntb-hours" class="input-field num" type="number" inputmode="decimal"
                   min="0" step="0.5" placeholder="0" />
          </label>
          <label class="field">
            <span class="field-label">Direct cost (USD)</span>
            <input id="ntb-cost" class="input-field num" type="number" inputmode="decimal"
                   min="0" step="1" placeholder="0" />
          </label>
        </div>

        <div class="grid-2">
          <label class="field">
            <span class="field-label">Agency involved</span>
            <select id="ntb-agency" class="input-field">
              ${AGENCIES.map((a) => html`<option value="${a}">${a}</option>`)}
            </select>
          </label>
          <label class="field">
            <span class="field-label">Goods (optional)</span>
            <input id="ntb-goods" class="input-field" type="text" maxlength="120"
                   placeholder="e.g. containerised electronics" />
          </label>
        </div>

        <label class="field">
          <span class="field-label">What happened, in your words</span>
          <textarea id="ntb-description" class="input-field" rows="4" maxlength="1200"
                    placeholder="Be factual and specific: what was demanded, by whom, on what stated basis, and how long it took."></textarea>
          <span class="field-hint">
            Facts resolve barriers; adjectives do not. Name the requirement, not the person.
          </span>
        </label>

        <label class="field">
          <span class="field-label">Resolution sought (optional)</span>
          <input id="ntb-resolution" class="input-field" type="text" maxlength="240"
                 placeholder="e.g. publication of the fee schedule at this post" />
        </label>

        <label class="row" style="gap: var(--ca-space-3); cursor: pointer; align-items: flex-start; margin-bottom: var(--ca-space-4);">
          <input type="checkbox" id="ntb-recurring"
                 style="margin-top: 3px; width: 17px; height: 17px; accent-color: var(--brand-accent);" />
          <span style="flex:1; font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
            This happens repeatedly, not just once
          </span>
        </label>

        <div id="ntb-error"></div>

        <button class="btn btn-primary btn-block" id="ntb-save" type="button">Save to my log</button>
      </div>
    </details>

    <div id="ntb-log"></div>
  `;

  const typeSelect = page.querySelector('#ntb-type');
  const catHint = page.querySelector('#ntb-cat-hint');
  const paintHint = () => {
    const type = barrierType(typeSelect.value);
    const cat = type ? NTB_CATEGORIES[type.category] : null;
    catHint.textContent = cat ? `Category ${type.category} — ${cat.label}. ${cat.hint}` : '';
  };
  typeSelect.addEventListener('change', paintHint);
  paintHint();

  const dateInput = page.querySelector('#ntb-date');
  dateInput.value = new Date().toISOString().slice(0, 10);

  const refresh = () => loadLog(page);
  page.querySelector('#ntb-save').addEventListener('click', () => save(page, refresh));

  refresh();
  return page;
}

async function save(page, refresh) {
  const val = (id) => page.querySelector(`#${id}`).value.trim();

  const barrier = {
    id: newId(),
    typeId: page.querySelector('#ntb-type').value,
    location: val('ntb-location'),
    date: val('ntb-date'),
    hoursLost: parseNumber(val('ntb-hours')) ?? 0,
    costUsd: parseNumber(val('ntb-cost')) ?? 0,
    agency: val('ntb-agency'),
    goods: val('ntb-goods'),
    description: val('ntb-description'),
    resolutionSought: val('ntb-resolution'),
    recurring: page.querySelector('#ntb-recurring').checked,
    createdAt: new Date().toISOString()
  };

  const errors = [];
  if (!barrier.description || barrier.description.length < 20) {
    errors.push('Describe what happened in at least a sentence — a vague report cannot be acted on.');
  }
  if (!barrier.date) errors.push('Give the date it happened.');

  const errorSlot = page.querySelector('#ntb-error');
  if (errors.length > 0) {
    errorSlot.innerHTML = html`
      <div class="notice notice-danger" style="margin-bottom: var(--ca-space-4);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div>
          <ul style="margin: 0 0 0 var(--ca-space-4); font-size: var(--ca-text-xs);">
            ${errors.map((e) => html`<li>${e}</li>`)}
          </ul>
        </div>
      </div>
    `;
    return;
  }
  errorSlot.innerHTML = '';

  const stored = Boolean(await saveRecord('barrier', barrier));
  if (!stored) {
    toast('Could not save — local storage is unavailable.', { tone: 'error' });
    return;
  }

  page.querySelector('#ntb-description').value = '';
  page.querySelector('#ntb-resolution').value = '';
  page.querySelector('#ntb-goods').value = '';
  page.querySelector('#ntb-hours').value = '';
  page.querySelector('#ntb-cost').value = '';
  page.querySelector('#ntb-recurring').checked = false;
  page.querySelector('#ntb-form-wrap').open = false;

  toast('Barrier saved to your log');
  refresh();
}

async function loadLog(page) {
  const summaryEl = page.querySelector('#ntb-summary');
  const logEl = page.querySelector('#ntb-log');

  let barriers = [];
  try {
    barriers = await idb.getAll('barriers');
  } catch {
    summaryEl.innerHTML = html`
      <div class="notice notice-warning" style="margin-bottom: var(--ca-space-5);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div style="font-size: var(--ca-text-sm);">
          Local storage is unavailable, so barriers cannot be logged. You can still fill the
          form and copy a submission.
        </div>
      </div>
    `;
    return;
  }

  barriers.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const stats = summarise(barriers);

  summaryEl.replaceChildren();
  logEl.replaceChildren();
  logEl.id = 'ntb-log';

  if (barriers.length === 0) {
    logEl.innerHTML = html`
      <div class="empty-state">
        <h3>No barriers logged yet</h3>
        <p>
          Record one as it happens — at the weighbridge, at the border, at the counter. The
          detail you remember at the time is the detail that makes a report actionable.
        </p>
      </div>
    `;
    return;
  }

  /* ---- Your NTB tax ---- */
  const summary = document.createElement('div');
  summary.id = 'ntb-summary-card';
  summary.innerHTML = html`
    <div class="card avoid-break" style="margin-bottom: var(--ca-space-5);">
      <h2 style="font-size: var(--ca-text-base); margin-bottom: var(--ca-space-4);">
        What barriers have cost you
      </h2>
      <div class="grid-2" style="margin-bottom: var(--ca-space-4);">
        <div>
          <div class="stat-figure num" style="font-size: var(--ca-text-xl);">${number(stats.hours, 1)}</div>
          <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
            hours lost across <span class="num">${stats.count}</span>
            ${stats.count === 1 ? 'report' : 'reports'}
          </div>
        </div>
        <div>
          <div class="stat-figure num" style="font-size: var(--ca-text-xl);">${money(stats.cost)}</div>
          <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
            in direct cost
          </div>
        </div>
      </div>
      ${stats.topLocations.length > 0
        ? html`
            <h2 class="section-title" style="margin-bottom: var(--ca-space-2);">Worst points</h2>
            <div class="stack" style="gap: var(--ca-space-2);">
              ${stats.topLocations.map(
                ([location, data]) => html`
                  <div class="row" style="font-size: var(--ca-text-sm);">
                    <span style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">
                      ${location}
                    </span>
                    <span class="num" style="color: var(--ca-text-tertiary); font-size: var(--ca-text-xs);">
                      ${number(data.hours, 1)} h · ${money(data.cost)} · ${data.count}×
                    </span>
                  </div>
                `
              )}
            </div>
          `
        : ''}
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary); margin-top: var(--ca-space-4);">
        This total is the argument. An association or corridor authority can act on
        “<span class="num">${number(stats.hours, 1)}</span> hours and
        ${money(stats.cost)} across <span class="num">${stats.count}</span> documented
        incidents”; it cannot act on “the weighbridges are bad”.
      </p>
    </div>
  `;
  summaryEl.appendChild(summary);

  /* ---- The log ---- */
  const heading = document.createElement('div');
  heading.className = 'section-title';
  heading.textContent = `Your log (${barriers.length})`;
  logEl.appendChild(heading);

  for (const barrier of barriers) logEl.appendChild(barrierCard(barrier, () => loadLog(page)));

  logEl.appendChild(
    PrintButton({
      targetId: 'ntb-log',
      title: 'Non-Tariff Barrier Log',
      subtitle: `${stats.count} incidents · ${number(stats.hours, 1)} hours · ${money(stats.cost)}`
    })
  );
}

function barrierCard(barrier, refresh) {
  const type = barrierType(barrier.typeId);
  const category = type ? NTB_CATEGORIES[type.category] : null;

  const card = document.createElement('article');
  card.className = 'card avoid-break';
  card.style.marginBottom = 'var(--ca-space-3)';

  card.innerHTML = html`
    <div class="row" style="align-items: flex-start; margin-bottom: var(--ca-space-2);">
      <div style="flex:1; min-width:0;">
        <h3 style="font-size: var(--ca-text-sm); margin-bottom: 2px;">
          ${type ? type.label : 'Barrier'}
        </h3>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary);">
          ${barrier.location} · ${shortDate(barrier.date)}
          ${category ? html` · Category ${type.category}` : ''}
        </div>
      </div>
      ${barrier.recurring
        ? html`<span class="badge" style="background: color-mix(in srgb, var(--ca-warning) 13%, transparent); color: var(--ca-warning);">
            Recurring
          </span>`
        : ''}
    </div>

    <div class="row no-print" style="gap: var(--ca-space-3); flex-wrap: wrap; margin-bottom: var(--ca-space-3);
                                     font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
      ${barrier.hoursLost > 0
        ? html`<span class="row" style="gap:4px;">${raw(icon('clock', 13))} <span class="num">${number(barrier.hoursLost, 1)} h</span></span>`
        : ''}
      ${barrier.costUsd > 0
        ? html`<span class="row" style="gap:4px;">${raw(icon('coins', 13))} <span class="num">${money(barrier.costUsd)}</span></span>`
        : ''}
      <span class="row" style="gap:4px;">${raw(icon('shield', 13))} ${barrier.agency}</span>
    </div>

    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
      ${barrier.description}
    </p>

    <div class="row no-print" style="gap: var(--ca-space-2); flex-wrap: wrap;">
      <button class="btn btn-secondary btn-sm" data-copy type="button">Copy submission</button>
      <button class="btn btn-ghost btn-sm" data-view type="button">View text</button>
      <div class="spacer"></div>
      <button class="btn btn-ghost btn-sm" data-delete type="button"
              style="color: var(--ca-danger);" aria-label="Delete this report">
        ${raw(icon('close', 15))}
      </button>
    </div>

    <pre data-text hidden style="margin-top: var(--ca-space-3); padding: var(--ca-space-3);
         background: var(--ca-surface-sunk); border-radius: var(--ca-radius-md);
         font-size: var(--ca-text-2xs); white-space: pre-wrap; overflow-x: auto;
         font-family: ui-monospace, monospace;"></pre>
  `;

  const submission = formatSubmission(barrier);
  const pre = card.querySelector('[data-text]');
  pre.textContent = submission;

  card.querySelector('[data-view]').addEventListener('click', (event) => {
    pre.hidden = !pre.hidden;
    event.target.textContent = pre.hidden ? 'View text' : 'Hide text';
  });

  card.querySelector('[data-copy]').addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(submission);
      toast('Submission copied — paste it at tradebarriers.africa');
    } catch {
      // Clipboard API needs a secure context and permission; reveal the
      // text instead of failing silently.
      pre.hidden = false;
      toast('Copy blocked by the browser — select the text below', { tone: 'error' });
    }
  });

  card.querySelector('[data-delete]').addEventListener('click', async () => {
    // .catch(() => null) swallowed the failure and then announced
    // success anyway, which is the same lie the bare await told.
    if (await idb.deleteQuiet('barriers', barrier.id)) {
      toast('Report deleted');
      refresh();
    } else {
      toast('Could not delete it — storage is unavailable on this device.', { tone: 'error' });
    }
  });

  return card;
}
